import { Box, styled, Typography } from "@mui/material";
import FrontImage from "@/../public/img/ranvet/widget-front-simple.png";
import Image from "next/image";
import LogoImage from "@/../public/img/ranvet/logo-without-tagline.png";
import { useState } from "react";
import PillButton from "@/app/shared/PillButton";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { CreateAnalyticEvent } from "@/actions/details/CreateAnalyticEvent";

const WIDTH = 176;

const Stage = styled(Box)({
  width: WIDTH,
  perspective: 1000,
});

const Card = styled(Box)({
  position: "relative",
  width: "100%",
  height: "100%",
  cursor: "pointer",
  transformStyle: "preserve-3d",
  transition: "transform 0.8s cubic-bezier(0.65,0.05,0.36,1)",
  "&.flipped": {
    transform: "rotateY(180deg)",
  },
});

const Face = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    width: "100%",
    height: "100%",
    backfaceVisibility: "hidden",
    position: "absolute",
    top: 0,
    left: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: 1,
    paddingTop: 2,
    whiteSpace: "nowrap",
    border: "1px solid",
    borderColor: "grey.300",
    borderRadius: 2,
    cursor: "pointer",
    "&.back": {
      transform: "rotateY(180deg)",
    },
  }),
);

function CardLabel() {
  return (
    <Typography
      sx={{
        backgroundColor: "white",
        position: "absolute",
        top: -6,
        left: "50%",
        transform: "translateX(-50%)",
        padding: "2px 6px",
        fontWeight: "bold",
        lineHeight: "0.5",
      }}
      variant="caption"
    >
      Nutrition
    </Typography>
  );
}

const CardText = styled(Typography)({
  textAlign: "center",
  whiteSpace: "normal",
  lineHeight: 1.3,
});

export function AdNutritionWidget({ adId }: { adId: string }) {
  const [isFlipped, setIsFlipped] = useState(false);
  return (
    <Stage
      sx={{
        height: isFlipped ? 362 : 255,
        transition: "height 0.8s cubic-bezier(0.65,0.05,0.36,1)",
      }}
    >
      <Card
        className={`card ${isFlipped ? "flipped" : ""}`}
        onClick={() => {
          const newIsFlipped = !isFlipped;
          setIsFlipped(newIsFlipped);
          CreateAnalyticEvent({
            event: {
              adIds: [adId],
              eventType: "CLICK#BOX",
              metadata: {
                newIsFlipped,
              },
            },
          });
        }}
      >
        <Face
          className="front"
          sx={{
            gap: 1,
          }}
        >
          <CardLabel />
          <CardText
            variant="caption"
            sx={{
              fontWeight: "bold",
            }}
          >
            LET US HELP YOU
            <br />
            START THE JOURNEY
          </CardText>
          <Image
            src={FrontImage}
            alt="From Race Day to Every Day"
            width={140}
          />
          <CardText
            variant="caption"
            sx={{
              fontWeight: "bold",
            }}
          >
            START NOW WITH...
          </CardText>
          <Box
            component={Image}
            src={LogoImage}
            alt="Ranvet"
            width={120}
            sx={{
              height: "auto",
              marginTop: 2,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              backgroundColor: "white",
              paddingLeft: 1,
              paddingRight: 1,
            }}
          >
            <SwapHorizIcon />
            Tap to Flip
          </Typography>
        </Face>
        <Face className="back" sx={{ gap: 2 }}>
          <CardLabel />
          <CardText
            variant="caption"
            sx={{
              fontWeight: "bold",
            }}
          >
            START YOUR THOROUGHBREDS NEW CHAPTER WITH THE RIGHT KNOWLEDGE AND
            SUPPORT
          </CardText>
          <CardText variant="caption">
            Everything you need to guide the transition of your thoroughbred
            from the race track, to the arena. Phase-by-phase feeding plans,
            targeted nutrition support, and expert advice on caring for your
            off-the-track horse. Unbiased, practical, trusted support all in one
            place.
          </CardText>
          <Box
            component="a"
            href="/hub/off-the-track"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              marginTop: "auto",
            }}
          >
            <PillButton
              primary
              sx={{
                backgroundColor: "#da291c",
                whiteSpace: "normal",
                lineHeight: 1.2,
              }}
              onClick={(e) => {
                e.stopPropagation();
                CreateAnalyticEvent({
                  event: {
                    adIds: [adId],
                    eventType: "CLICK#LINK#OUT",
                    metadata: {
                      href: "/hub/off-the-track",
                      text: "OFF THE TRACK HEALTH HUB",
                    },
                  },
                });
              }}
            >
              OFF THE TRACK HEALTH HUB
            </PillButton>
          </Box>
        </Face>
      </Card>
    </Stage>
  );
}
